import React from 'react';
import { ArrowRight, ThermometerSnowflake, Snowflake, Fan, Droplets, Power, Volume2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const PROBLEMS = [
  {
    title: 'AC Blowing Warm Air',
    slug: '/ac-blowing-warm-air',
    summary: 'Your system runs but only blows warm or hot air. Common causes include dirty filters, low refrigerant, and thermostat issues.',
    icon: ThermometerSnowflake,
    color: 'text-red-500',
    bg: 'bg-red-50',
  },
  {
    title: 'AC Freezing Up',
    slug: '/ac-freezing-up',
    summary: 'Ice forming on coils or refrigerant lines signals airflow problems, refrigerant leaks, or a dirty evaporator coil.',
    icon: Snowflake,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
  {
    title: 'AC Running But Not Cooling',
    slug: '/ac-running-but-not-cooling',
    summary: 'The system never shuts off but your house stays warm. Could be an undersized unit, duct leaks, or a failing compressor.',
    icon: Fan,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
  },
  {
    title: 'AC Leaking Water',
    slug: '/ac-leaking-water',
    summary: 'Water pooling around the indoor unit typically means a clogged drain line, cracked pan, or frozen coil thawing.',
    icon: Droplets,
    color: 'text-cyan-500',
    bg: 'bg-cyan-50',
  },
  {
    title: 'AC Not Turning On',
    slug: '/ac-not-turning-on',
    summary: 'No sound, no airflow — often caused by a tripped breaker, dead thermostat batteries, or a failed capacitor.',
    icon: Power,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
  },
  {
    title: 'AC Making Loud Noise',
    slug: '/ac-making-loud-noise',
    summary: 'Banging, screeching, buzzing, or hissing sounds each point to different mechanical or electrical issues.',
    icon: Volume2,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
  },
];

const CommonAcProblemsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="common-problems-heading"
          >
            Common AC Problems in Louisiana
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Louisiana's heat and humidity push air conditioning systems to their limits. Here are the
            most common problems homeowners face — and what to do about each one.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Problem Grid */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {PROBLEMS.map((problem, i) => (
              <a
                key={i}
                href={problem.slug}
                data-testid={`problem-card-${i}`}
                className="group flex items-start gap-5 p-6 bg-white rounded-2xl border border-slate-200 hover:border-[#0EA5E9] hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-12 h-12 ${problem.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <problem.icon className={`w-6 h-6 ${problem.color}`} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-[#0F172A] mb-1 group-hover:text-[#0EA5E9] transition-colors flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {problem.title}
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </h2>
                  <p className="text-slate-500 text-sm">{problem.summary}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Louisiana Is Different */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why AC Problems Are More Common in Louisiana
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl text-center">
              <p className="text-3xl font-bold text-[#F97316]" style={{ fontFamily: 'Outfit, sans-serif' }}>80%+</p>
              <p className="text-slate-500 text-sm mt-1">Average summer humidity</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <p className="text-3xl font-bold text-[#0EA5E9]" style={{ fontFamily: 'Outfit, sans-serif' }}>8+ mo</p>
              <p className="text-slate-500 text-sm mt-1">AC runs per year</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <p className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>12–15 yr</p>
              <p className="text-slate-500 text-sm mt-1">Average system lifespan</p>
            </div>
          </div>
          <div className="prose prose-slate max-w-none">
            <p>
              Gulf Coast climates put extreme stress on HVAC systems. High humidity forces your AC to work
              overtime removing moisture, salt air corrodes outdoor components, and the sheer number of
              operating hours means parts wear out faster than the national average.
            </p>
            <p>
              That's why it's important to know when a repair makes sense — and when it's time to invest in
              a new, more efficient system. Learn about{' '}
              <a href="/ac-unit-lifespan-louisiana" className="text-[#0EA5E9]">how long AC units last in Louisiana</a>{' '}
              or use our{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">AC age checker</a>{' '}
              to find your system's manufacture date.
            </p>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Facing a decision about your system? Read our{' '}
              <a href="/repair-vs-replace-ac" className="text-[#0EA5E9]">repair vs. replace guide</a>{' '}
              or check{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>.
              For transparent pricing, see our{' '}
              <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing page</a>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Explore Your Options?
          </h2>
          <p className="text-slate-300 mb-8">
            Get an instant, no-obligation estimate for your HVAC replacement. Fast, free, online.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommonAcProblemsPage;
